import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { FaceSmileIcon, XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { Button, Text } from '../../ui'
import { SocialPost } from '../../../types/social'
import AuthorAvatar from './AuthorAvatar'
import CommentList from './CommentList'
import MentionText from './MentionText'
import PostImages from './PostImages'
import ReactionBar, { ReactionSummary } from './ReactionBar'
import { commentBubbleClass, formatSocialDate } from './socialUtils'
import { formatAuthorName } from '../../../utils/userDisplayName'

export interface PostDetailModalProps {
  post: SocialPost
  onClose: () => void
  onDeleteComment: (commentId: string) => void | Promise<void>
  canDeleteComments?: boolean
  interactive?: boolean
  currentUserId?: string
  currentUserName?: string | null
  currentUserAvatar?: string | null
  onAddComment?: (content: string) => void | Promise<void>
  onUpdateComment?: (commentId: string, content: string) => void | Promise<void>
  onReact?: (emoji: string) => void | Promise<void>
}

const formatDetailDate = (dateString: string) =>
  new Date(dateString).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const PostDetailModal: React.FC<PostDetailModalProps> = ({
  post,
  onClose,
  onDeleteComment,
  canDeleteComments = false,
  interactive = false,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  onAddComment,
  onUpdateComment,
  onReact,
}) => {
  const reactions = post.reactions || []
  const comments = post.comments || []
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null)
  const [showReactions, setShowReactions] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [reactionError, setReactionError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleReact = async (emoji: string) => {
    if (!onReact) return
    try {
      setSubmitting(true)
      setReactionError(null)
      await onReact(emoji)
      setShowReactions(false)
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Erro ao reagir')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!canDeleteComments && !interactive) return
    if (!window.confirm('Excluir este comentário?')) return
    try {
      setDeletingCommentId(commentId)
      setActionError(null)
      await onDeleteComment(commentId)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao excluir comentário')
    } finally {
      setDeletingCommentId(null)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl min-h-[70vh] max-h-[92vh] overflow-hidden rounded-xl bg-white dark:bg-gray-800 shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Detalhes da postagem</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Postagem fixa */}
        <div className="shrink-0 px-5 sm:px-6 py-5 space-y-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex gap-3">
            <AuthorAvatar
              name={post.author?.name}
              avatarPath={post.author?.avatar_url}
            />
            <div className="min-w-0 pt-0.5">
              <Text size="sm" className="font-semibold text-gray-900 dark:text-gray-100">
                {formatAuthorName(post.author)}
              </Text>
              <Text size="xs" color="muted" className="mt-0.5">
                {formatDetailDate(post.created_at)}
                {post.edited_at && ' · Editado'}
              </Text>
            </div>
          </div>

          {post.content.trim() ? (
            <p className="text-[15px] leading-relaxed text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
              <MentionText content={post.content} />
            </p>
          ) : null}

          <PostImages images={post.images} />

          {(reactions.length > 0 || (interactive && onReact)) && (
            <div className="space-y-2">
              {reactions.length > 0 && <ReactionSummary reactions={reactions} iconSize="md" />}

              {interactive && onReact && (
                <>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => setShowReactions((v) => !v)}
                    className={clsx(
                      'flex items-center justify-center gap-2 w-full py-2.5 text-sm font-medium transition-colors rounded-lg border border-gray-200 dark:border-gray-700',
                      'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/40',
                      showReactions && 'text-primary-600 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/20'
                    )}
                  >
                    <FaceSmileIcon className="h-5 w-5" />
                    Reagir
                  </button>

                  {showReactions && (
                    <div className="px-1 py-2.5 rounded-lg bg-gray-50/50 dark:bg-gray-900/20">
                      <ReactionBar
                        reactions={reactions}
                        currentUserId={currentUserId}
                        disabled={submitting}
                        variant="actions"
                        onReact={handleReact}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {reactionError && (
            <Text size="xs" className="text-red-600 dark:text-red-400">{reactionError}</Text>
          )}
        </div>

        {/* Comentários — única área com scroll */}
        <div className="flex flex-col flex-1 min-h-0">
          <div className="shrink-0 px-5 sm:px-6 pt-4 pb-2">
            <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
              Comentários ({comments.length})
            </Text>
          </div>

          {interactive && onAddComment && onUpdateComment ? (
            <CommentList
              comments={comments}
              currentUserId={currentUserId}
              currentUserName={currentUserName}
              currentUserAvatar={currentUserAvatar}
              expandedComments
              onAdd={onAddComment}
              onUpdate={onUpdateComment}
              onDelete={handleDeleteComment}
            />
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 pb-4">
              {comments.length === 0 ? (
                <Text size="sm" color="muted">Nenhum comentário nesta postagem.</Text>
              ) : (
                <div className="space-y-3">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex gap-2 items-start">
                      <AuthorAvatar
                        name={comment.author?.name}
                        avatarPath={comment.author?.avatar_url}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <div className={clsx(commentBubbleClass, 'inline-block max-w-full')}>
                          <Text size="xs" className="font-semibold text-gray-900 dark:text-gray-100">
                            {formatAuthorName(comment.author)}
                          </Text>
                          <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">
                            <MentionText content={comment.content} />
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 ml-1">
                          <Text size="xs" color="muted">
                            {formatSocialDate(comment.created_at)}
                            {comment.edited_at && ' · Editado'}
                          </Text>
                          {canDeleteComments && (
                            <button
                              type="button"
                              onClick={() => void handleDeleteComment(comment.id)}
                              disabled={deletingCommentId === comment.id}
                              className="text-xs font-medium text-gray-500 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                            >
                              Excluir
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {actionError && (
                <Text size="xs" className="text-red-600 dark:text-red-400 mt-2">{actionError}</Text>
              )}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end shrink-0">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default PostDetailModal
